// File: typescript/apps/front-end/src/main/react/discussion/DiscussionContentDisplayer.tsx
import React, {useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Localizer} from "../Types";
import RelativeDateFormatter, {DateFormatterDisplay} from "@/main/common/DateFormatter";
import DiscussionButtons from "@/main/react/discussion/DiscussionButtons";
import type {DiscussionMobileSectionNavigation} from "@/main/react/discussion/DiscussionMobileActionBar";
import {DiscussionViewTracker} from "@/main/discussion/DiscussionViewTracker";
import {DiscussionReadCompletionTracker} from "@/main/discussion/DiscussionReadCompletionTracker";
import {MediaAttentionTracker} from "@/main/react/discussion/MediaAttentionTracker";
import {DiscussionVersionHistory} from "@/main/react/discussion/DiscussionVersionHistory";
import {MetaHandler} from "@/main/common/MetaHandler";
import {showThoughtTextEditorDialog} from "@/main/react/editor/thought/ThoughtTextEditorDialog";
import type {DialogController} from "@/main/react/Dialog/Dialog";
import {CtaButton} from "@/main/react/buttons/CtaButton";
import {Button} from "@/components/ui/button";
import {HighlightPopover, useHighlightPopover} from "@omsimos/react-highlight-popover";
import { ChevronsDownUp, ChevronsUpDown, Link2, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { buildTextFragmentUrl } from "@/main/util/PlatformIdentifierUtil";
import {getDisplayUsername, getUserProfilePath} from "@/main/common/UsernameDisplayUtils";
import logger from "@/lib/logger";
import { useBodyMediaLightbox } from "@/main/react/media/useBodyMediaLightbox";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import type {QuestionState, QuestionStateView} from "@/main/common/QuestionFacetTypes";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { localizeWithMemberName } from "@/main/react/component/localizeWithMemberName";
import ReviewHeader from "@/main/react/discussion/facet/ReviewHeader";
import { getDeletedContentReasonLabel } from "@/main/react/contentDeletion/ContentDeletionLocalization";
import { DeletedContentRestoreRow } from "@/main/react/page/profile-page/DeletedContentRestoreRow";
import {cn} from "@/lib/utils";
import DiscussionFootnotePreviews from "@/main/react/discussion/DiscussionFootnotePreviews";
import { Z_INDEX_LAYER_STICKY } from "@/main/common/ZIndexLayers";
import { buildSeriesDiscussionHref, type SeriesReadingContext } from "@/main/react/series/SeriesReadingTypes";
import PassageMarkers from "@/main/react/discussion/passage/PassageMarkers";
import { PassageCommentAffordance } from "@/main/react/discussion/passage/PassageCommentAffordance";
import {
    ACCOUNT_ACCESS_DIALOG_COPY,
    buildWriteThoughtContinuation,
    resolveContextualAccountAccessDialogOptions,
} from "@/main/react/account_access/AccountAccessDialogContext";
import { openAccountAccessDialog } from "@/main/react/account_access/AccountAccessDisplayerDialog";
import { usePassageBlockIds } from "@/main/react/discussion/passage/usePassageBlockIds";
import { mapBlocksToDom, passageBlockMapsEqual, type PassageAnchoredThoughtSummary, type PassageBlockMap } from "@/main/react/discussion/passage/mapBlocksToDom";
import { registerPassageBlocks } from "@/main/react/discussion/passage/passageBlockRegistry";
import { ReadingTimeLabel, hasReadingTime } from "@/main/react/discussion/ReadingTimeLabel";
import { shareTitleFromHtml } from "@/main/react/discussion/shareTitleFromHtml";
import TranslateControlLazy from "@/main/translation/TranslateControlLazy";
import type { TranslationResult } from "@/main/translation/TranslateControl";
import { hasTranslatorBackend } from "@/main/translation/translatorDetection";
import { DistinctionBadge } from "@/main/react/distinction/DistinctionBadge";

export type BaseProps = {
    localizer: Localizer;
    discussionPublicId: string;
    discussionSlug: string;
    /**
     * The post's topics, MIRRORED from the crawlable `<section>` in `discussion.jte` (tier-2 finding
     * 23). That block sits inside the island's own container, so `createRoot()` discards it on mount
     * and the chips survived only the no-JS pass — which, since Google and Bing both render, is close
     * to nobody.
     *
     * <p>Mirrored, never moved: rule 32 is that JS may only improve on the HTML we shipped. Deleting
     * the JTE block to "avoid duplication" would regress the crawlable pass.
     */
    tagNames?: string[];
    discussionGroupsSummary?: React.ReactNode;
    discussionVisibilityBanner?: React.ReactNode;
    /**
     * The BOARD facet's grid, rendered INSIDE this card above the body.
     *
     * <p>A slot rather than a sibling because that is the only arrangement that matches what the server
     * already sends. `discussion.jte` renders the grid inside the post card above the body (master design
     * §4.1d, §18.1); the island rendered it after this whole component until 2026-08-13, so every board
     * post visibly reflowed on hydration and the board read as a second, unrelated card. The two paths
     * must occupy the same box.
     *
     * <p>Kept as a `ReactNode` rather than board props so this component learns nothing about the facet:
     * `DiscussionPage` owns the lazy boundary, the error boundary and the payload.
     */
    boardSlot?: React.ReactNode;
    /**
     * `BOARD-21` (round-41 D17): render `boardSlot` AFTER the body instead of above it. Set for a COMPLETE
     * board only, where the body (what is being ranked, by what standard) is the reader's first question;
     * a live board keeps §4.1d's grid-first order. `discussion.jte` makes the same choice from the same
     * `complete` bit, so the two paths still occupy the same box (rule 32).
     */
    boardAfterBody?: boolean;
    /** The POLL facet's island, on the board slot's exact terms; rendered directly after it. */
    pollSlot?: React.ReactNode;
    authorUsername?: string;
    authorProfileImageUrl?: string;
    /**
     * The post's stored text length (`discussions.body_text_length`, the server's generated column), for
     * the reading-time token in the byline (round-6 `2a`, D5 / A4). Absent renders no label: the value is
     * the server's, never measured here from `contentHtml`. Design: `2026-09-06-read-surface-completion…` §3.
     */
    bodyTextLength?: number | null;
    createdAt?: string;
    updatedAt?: string;
    /**
     * When this post's latest version was written, but ONLY when that happened after somebody had
     * already replied. Absent otherwise, which is the common case.
     *
     * <p>The server decides this; nothing here re-derives it. Marking every edited post would label an
     * author fixing a typo thirty seconds after publishing, and the marker exists for the case where a
     * reply now answers text that no longer exists. Design:
     * designs/01-now/2026-09-06-version-history-member-exposure-design.md §3.2.
     */
    editedAfterReplyAt?: string;
    /**
     * The author's byline distinction, already resolved by the server (distinctions design §6.1): a
     * distinction key, and the group name when the author is that group's current Voice of the month
     * (A6: it takes the slot). Absent renders nothing. Carried on both delivery paths by DiscussionPage.
     */
    authorFeaturedDistinction?: string;
    authorVoiceOfMonthGroupName?: string;
    agreementAgreementTitle: string;
    disagreementAgreementTitle: string;
    condensed?: boolean;
    title: string;
    privateAccess?: boolean;
    sensitive?: boolean;
    isArchived?: boolean;
    archivedAt?: string;
    isDeleted?: boolean;
    deletedAt?: string;
    deletedByModerator?: boolean;
    deletedReasonType?: string;
    deletedMessage?: string;
    agreementsCount: number;
    disagreementsCount: number;
    argumentsCount?: number;
    addedToSeriesCount?: number;
    initiallyAgreeing?: boolean;
    initiallyDisagreeing?: boolean;
    initiallyAddedToAnySeries?: boolean;
    initiallySavedToBookmarks?: boolean;
    canEdit?: boolean;
    contentHtml?: string | undefined;
    /**
     * The post's OWN language (e.g. "pt"), which differs from the surrounding UI locale whenever
     * someone reads a post published in another language. Rendered as `lang` on the body so screen
     * readers pronounce it correctly and the browsers' built-in translate offer can see it.
     * See designs/01-now/2026-07-21-discussion-content-language-tagging-design.md.
     */
    contentLanguageTag?: string;
    expandButtonHtml?: string | null;
    seriesReadingContext?: SeriesReadingContext;
    /**
     * QUESTION facet (design §11.1). `questionState` is the server's derivation; the two badges beside
     * the title mirror the ones `discussion.jte` server-renders, byte-for-byte in appearance, so
     * hydration does not flicker the framing. `onQuestionStateChanged` carries the `QuestionStateView`
     * returned by the mark-answered / reopen verbs back up to the page, which owns the live state.
     */
    isQuestion?: boolean;
    /**
     * CHANGED MY MIND: has anything on this post ever changed its author's mind?
     *
     * Renders the header badge beside the question badges below. One bit only - which thoughts carry
     * a mark is each thought's own flag, and a second copy here could disagree with it.
     */
    mindChanged?: boolean;
    questionState?: QuestionState;
    onQuestionStateChanged?: (view: QuestionStateView) => void;
    /** SV-130: forwarded to DiscussionButtons, which offers "Ask someone to answer" only when true. */
    askToAnswerAvailable?: boolean;
    /**
     * REVIEW facet. Renders a strip above the body naming what the post reviews and how it was rated.
     *
     * <p>A strip rather than badges beside the title, which is where QUESTION put its framing: a review
     * carries a PAYLOAD — a subject to link to and a rating to draw — and neither fits in a pill. The
     * subject link is also the feature's discovery loop, so it needs to be a real, obvious target.
     */
    reviewSubjectName?: string;
    reviewSubjectSlug?: string;
    reviewRatingHalfStars?: number;
    /** The subject's public id — not rendered, carried only so the edit dialog can seed itself. */
    reviewSubjectPublicId?: string;
    onReviewFacetChanged?: (next: { subjectName?: string; ratingHalfStars?: number }) => void;
    /**
     * `DISCUSSION-26` — forwarded straight to `DiscussionButtons`, which owns the mobile bar. Carried
     * through rather than rebuilt: only the page knows the section list and the selection handler, and
     * only `DiscussionButtons` renders the bar.
     */
    mobileSectionNavigation?: DiscussionMobileSectionNavigation;
    /**
     * SV-141 D1 (slice `1b`): the page's own `isShortPostPage`, passed down rather than re-derived from
     * `title` so the page and this card cannot disagree on what a short post is (A8). When true the
     * author's "Version history" is not offered and `DiscussionButtons` withholds every thought-composer
     * door. Default `false`: a long post renders exactly as before.
     */
    isShortPostPage?: boolean;
    /**
     * Round-33 `2b` (r33 D13, A12): the post's page name from the server (`pageName` on /page-data,
     * `data-page-name` on a direct load). Rendered ONLY when `isShortPostPage`, as a visually hidden span
     * inside the existing `<h1>`, the twin of `discussion.jte`'s, so a title-less short post's heading has an
     * accessible name and nothing changes on hydration (rule 32). Never re-derived from `title`.
     */
    pageName?: string;
    /**
     * PASSAGE anchors (`designs/00-inbox/2026-07-30-passage-anchored-comments-ux-design.md`, D4). One entry
     * per top-level block of the post's `source_json` in document order, the block's id or `null`; empty
     * when the post is not eligible. An array means the page has the server's answer (it came through
     * `/page-data`); `undefined` means this load did not carry it (the server-rendered dataset does not),
     * and `usePassageBlockIds` fetches it for a body long enough to be eligible.
     */
    passageBlockIds?: Array<string | null>;
    /**
     * The anchored thoughts currently loaded in the tree below, one summary each. Markers are drawn only
     * for blocks in this list; the list is the tree's, so the markers never disagree with the chips.
     */
    passageAnchoredThoughts?: PassageAnchoredThoughtSummary[];
};

/**
 * The reading measure of the article card (`DISCUSSION-17`).
 *
 * `ch` is the width of the digit zero, which in this typeface is wider than the mean glyph, so a `ch`
 * cap overshoots the character count it is chosen for. Measured off the 2026-08-13 desktop frames at
 * `78ch`: the opening paragraph rendered 81 characters on line 1 and 89 on line 2, a mean of 85
 * against the ~75 the 78 was picked to produce. 78 x (75 / 85) = 68.8, so `69ch` is the calibrated
 * value for this typeface.
 *
 * Kept in `ch` rather than converted to `rem`/`px` so the cap still tracks the body font size, and
 * pinned here as a constant asserted by exact equality — a cap edited back to a round number is
 * indistinguishable from a deliberate one at the call site.
 */
export const DISCUSSION_ARTICLE_MEASURE_CLASS = "lg:max-w-[69ch]";

/**
 * The height, in CSS pixels, at which the condensed article is clamped.
 *
 * Exported because the toggle's whole correctness condition is "the body is taller than this"
 * (`DISCUSSION-12`), and a test that re-declares the number proves nothing about the component.
 */
export const DISCUSSION_CONDENSED_MAX_HEIGHT_PX = 280;

type DiscussionSelectionPopoverProps = {
    selectionCommentLabel: string;
    onCommentSelection: (selectionText: string) => void;
    /**
     * PASSAGE anchors: the block join for this body, or null. Renders the "Comment on this passage"
     * affordance beside the quote button whenever the live selection sits inside one mapped block.
     */
    passageBlockMap: PassageBlockMap | null;
    onCommentOnPassage: (blockId: string, excerpt: string) => void;
    /** FI-585: copy the post URL with a text fragment for the selection. Absent: no control. */
    onCopyHighlightLink?: (selectionText: string) => void;
    localizer: Localizer;
};

function DiscussionSelectionPopover({
                                        selectionCommentLabel,
                                        onCommentSelection,
                                        onCopyHighlightLink,
                                        passageBlockMap,
                                        onCommentOnPassage,
                                        localizer,
                                    }: DiscussionSelectionPopoverProps) {
    const {currentSelection, setCurrentSelection, setShowPopover} = useHighlightPopover();

    const hidePopover = useCallback(() => {
        setShowPopover(false);
        setCurrentSelection("");
    }, [setCurrentSelection, setShowPopover]);

    const handleCommentClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const selectedQuote = currentSelection.trim();

        if (!selectedQuote) {
            hidePopover();
            return;
        }

        onCommentSelection(selectedQuote);
        hidePopover();
        window.getSelection()?.removeAllRanges();
    }, [currentSelection, hidePopover, onCommentSelection]);

    const clearSelectionAfterTap = useCallback(() => {
        hidePopover();
        window.getSelection()?.removeAllRanges();
    }, [hidePopover]);

    const handleCopyLinkClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const selectedText = currentSelection.trim();
        if (selectedText) {
            onCopyHighlightLink?.(selectedText);
        }
        clearSelectionAfterTap();
    }, [clearSelectionAfterTap, currentSelection, onCopyHighlightLink]);

    return (
        <div
            className="mt-2 flex select-none items-center gap-1 rounded-full border border-border bg-popover p-1 shadow-[var(--surface-shadow-md)]">
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-full border border-border bg-popover text-xs text-text-main hover:bg-muted"
                onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
                onClick={handleCommentClick}
            >
                <MessageSquarePlus className="size-3.5" aria-hidden={true}/>
                {selectionCommentLabel}
            </Button>
            {onCopyHighlightLink ? (
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="rounded-full border border-border bg-popover text-xs text-text-main hover:bg-muted"
                    onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    onClick={handleCopyLinkClick}
                    data-playwright-id="discussion-selection-copy-link"
                >
                    <Link2 className="size-3.5" aria-hidden={true}/>
                    {localizer("discussion.selection.copy_link")}
                </Button>
            ) : null}
            <PassageCommentAffordance
                localizer={localizer}
                blockMap={passageBlockMap}
                selectionText={currentSelection}
                onCommentOnPassage={onCommentOnPassage}
                onAfterTap={clearSelectionAfterTap}
            />
        </div>
    );
}

export function DiscussionContentDisplayer(props: BaseProps) {
    const {
        localizer,
        title,
        privateAccess,
        sensitive = false,
        isArchived = false,
        authorUsername,
        authorProfileImageUrl,
        bodyTextLength,
        createdAt,
        updatedAt,
        editedAfterReplyAt,
        authorFeaturedDistinction,
        authorVoiceOfMonthGroupName,
        deletedAt,
        deletedByModerator = false,
        deletedReasonType,
        deletedMessage,
        contentHtml,
        contentLanguageTag,
        agreementsCount,
        disagreementsCount,
        argumentsCount,
        addedToSeriesCount,
        initiallyAgreeing,
        initiallyDisagreeing,
        initiallyAddedToAnySeries,
        initiallySavedToBookmarks,
        agreementAgreementTitle,
        disagreementAgreementTitle,
        canEdit,
        discussionGroupsSummary,
        discussionVisibilityBanner,
        seriesReadingContext,
        isQuestion = false,
        mindChanged = false,
        questionState,
        onQuestionStateChanged,
        askToAnswerAvailable = false,
        reviewSubjectName,
        reviewSubjectSlug,
        reviewRatingHalfStars,
        reviewSubjectPublicId,
        onReviewFacetChanged,
        mobileSectionNavigation,
        isShortPostPage = false,
        pageName,
    } = props;

    // The state badge's copy key. `undefined` reads as unanswered, matching the server's own fallback in
    // `discussion.jte` — a question whose state we could not resolve is not "answered".
    const questionStateKey = questionState === "RESOLVED"
        ? "discussion.question.state.resolved"
        : questionState === "ANSWERED"
            ? "discussion.question.state.answered"
            : "discussion.question.state.unanswered";

    const [condensedLocal, setCondensedLocal] = useState<boolean>(!!props.condensed);
    // End-of-post band anchor (slice 1f). A callback ref into state, so DiscussionButtons re-renders with
    // the real element once it exists; it portals the reply row into it from its own state.
    const [endOfPostReplyRowAnchor, setEndOfPostReplyRowAnchor] = useState<HTMLDivElement | null>(null);
    const endOfPostAnchors = useMemo(() => ({replyRow: endOfPostReplyRowAnchor}), [endOfPostReplyRowAnchor]);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    // Body images: wrapped in their `/media/{id}` permalink (unchanged), and a plain left-click on one
    // now opens the full-screen viewer instead of leaving the discussion. The anchor and its href are
    // untouched, so a modified click, a crawler and a no-JS reader all keep today's behaviour.
    // See useBodyMediaLightbox.tsx and the design doc §3.2.
    const { ref: contentRootRef, lightbox: mediaLightbox } = useBodyMediaLightbox<HTMLDivElement>({
        hint: props.discussionPublicId ? { kind: "d", contentId: props.discussionPublicId } : null,
        contentHtml,
        localizer,
    });

    const dialogControllerRef = useRef<DialogController | null>(null);

    /**
     * `DISCUSSION-12`. One ref, two owners: the lightbox hook enhances the injected body nodes through
     * its own ref, and the collapse toggle has to measure the same element. A second wrapper div would
     * change the injected body's box, so the node is handed to both instead.
     */
    const setBodyNode = useCallback((node: HTMLDivElement | null) => {
        bodyRef.current = node;
        (contentRootRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }, [contentRootRef]);

    // compute once per render
    const isUserLoggedIn = MetaHandler.isUserLoggedIn();

    useEffect(() => {
        if (!props.discussionPublicId || props.isDeleted) {
            return;
        }

        const tracker = new DiscussionViewTracker(props.discussionPublicId);
        tracker.startViewTracking();

        return () => {
            if (typeof tracker.stopViewTracking === "function") {
                tracker.stopViewTracking();
            }
        };
    }, [props.discussionPublicId, props.isDeleted]);

    /**
     * Did they finish it? (`designs/01-now/2026-09-06-read-surface-completion-reading-time-and-author-stats-design.md` §2)
     *
     * Its OWN effect, not a second job inside the view tracker above. The design's requirement is that
     * completion "never blocks or delays the view call", and two effects that share no code path
     * satisfy that structurally rather than by care.
     *
     * Keyed on `contentHtml` as well as the post id: the body node is replaced when the HTML changes,
     * and an observer left watching a detached node reports on a body nobody is reading. The tracker
     * itself never reports twice and never reports after `stop()`.
     */
    useEffect(() => {
        if (!props.discussionPublicId || props.isDeleted) {
            return;
        }

        const completionTracker = new DiscussionReadCompletionTracker(props.discussionPublicId);
        completionTracker.start(bodyRef.current);

        return () => completionTracker.stop();
    }, [props.discussionPublicId, props.isDeleted, props.contentHtml]);

    /**
     * Did they look at the media? (`designs/01-now/2026-08-01-media-consumption-signal-capture-primitives-design.md`
     * §3.4, §4 P1-P4; round-5 slice `MD-2a`.)
     *
     * A third tracker with its own effect, for the same structural reason as the one above: nothing
     * passes between the three, so none can delay or break another. Keyed on `contentHtml` too: the
     * tracker collects the body's media items once at start, and a replaced body is a new set of
     * items. The lightbox and the gated embeds credit their opens into the session this tracker owns
     * (`MediaAttentionSession.recordMediaOpen`), so the page instance sends ONE beacon, at hide/unload.
     */
    useEffect(() => {
        if (!props.discussionPublicId || props.isDeleted) {
            return;
        }

        const attentionTracker = new MediaAttentionTracker(props.discussionPublicId);
        attentionTracker.start(bodyRef.current);

        return () => attentionTracker.stop();
    }, [props.discussionPublicId, props.isDeleted, props.contentHtml]);

    const dateFormatter = new RelativeDateFormatter(localizer);
    const isDeleted = Boolean(deletedAt);
    const isReadOnly = isArchived || isDeleted;

    const displayAuthorUsername = getDisplayUsername(authorUsername, localizer);
    const authorProfilePath = getUserProfilePath(authorUsername);

    const formattedCreated = createdAt ? dateFormatter.timeSince(createdAt) : "";
    const formattedUpdated = updatedAt ? dateFormatter.timeSince(updatedAt) : "";
    // QA-18: the absolute dates behind the byline's relative stamps, as their <time title>s.
    const createdAtAbsolute = useMemo(
        () => (createdAt ? new RelativeDateFormatter(localizer).formatDateTime(createdAt) : undefined),
        [createdAt, localizer],
    );
    const updatedAtAbsolute = useMemo(
        () => (updatedAt ? new RelativeDateFormatter(localizer).formatDateTime(updatedAt) : undefined),
        [localizer, updatedAt],
    );
    const startedLabel = formattedCreated ? localizer("discussion.started_at", formattedCreated) : "";
    const deletedReasonLabel = getDeletedContentReasonLabel(localizer, deletedReasonType);
    /**
     * `EMPTY-STATES-78` door 3 (D33): the author of a SELF-deleted post gets the author tombstone and
     * the restore control. Gated on `!deletedByModerator` because that is the server's own predicate —
     * `restoreOwnDeletedDiscussion` refuses moderator deletions (`isSelfDeleted`), so the author copy
     * ("Restore it to put it back where it was") and the control must not appear on a post the author
     * cannot restore. Computed here rather than plumbed as a prop: `discussion.jte` derives the same
     * answer from the same two facts (logged-in username equals author username), so the SSR branch and
     * this one cannot disagree on hydration.
     */
    const viewerIsSelfDeletingAuthor = isDeleted && !deletedByModerator
        && Boolean(authorUsername) && MetaHandler.getLoggedInUsername() === authorUsername;
    const resolvedDeletedMessage = deletedByModerator
            ? deletedReasonLabel
                ? localizer("discussion.deleted.message_moderator_with_reason", deletedReasonLabel)
                : localizer("discussion.deleted.message_moderator")
            : viewerIsSelfDeletingAuthor
                ? localizer("discussion.deleted.message_author")
                : deletedMessage ?? localizer("discussion.deleted.message");
    const deletedRelativeLabel = deletedByModerator
        ? deletedReasonLabel
            ? localizer("discussion.deleted.by_moderator_relative_time_with_reason", deletedReasonLabel)
            : localizer("discussion.deleted.by_moderator_relative_time")
        : localizer("discussion.deleted.by_author_relative_time");

    const updatedLabel = localizer("discussion.updated_at", formattedUpdated);
    const showUpdatedLabel = Boolean(formattedUpdated && formattedUpdated !== formattedCreated);
    /**
     * The edited marker is ATTRIBUTION (rule 23): a statement about the post, carrying no control of
     * any kind, and no link either — the version history view it would one day point at does not exist
     * yet, and inventing a destination is the same rule broken from the other side.
     *
     * <p>⚠️ It still carries no link, and that has not changed with the arrival of the history view
     * below it (2026-09-09, slice `2c`). The panel is the AUTHOR's, owner-gated, and the marker is
     * shown to everyone — turning a statement every reader sees into a control only one of them can
     * use is the rule broken from a third side.
     *
     * <p>The label says "after replies" rather than a bare "Edited" on purpose. The byline already
     * renders `discussion.updated_at` whenever the relative update time differs from the relative
     * publish time, and the server-side condition guarantees the edit is meaningfully later than
     * publication, so a bare "Edited" beside "Updated 2 days ago" would read as a duplicate and carry
     * none of the integrity meaning that justifies the field.
     */
    const editedAfterReplyLabel = editedAfterReplyAt
        ? localizer("discussion.edited_after_replies")
        : "";
    const displayTitle = isDeleted
        ? localizer(viewerIsSelfDeletingAuthor ? "discussion.deleted.title_author" : "discussion.deleted.title")
        : title;
    // Share payload only (short-posts design D5, H0-17): a title-less (short) post is named by its own
    // plain text, as the card does. The share chokepoint bounds the length; the heading is untouched.
    const shareTitle = useMemo(() => shareTitleFromHtml(title, contentHtml), [title, contentHtml]);
    const readMore = localizer("literal.read-more");
    const readLess = localizer("literal.read-less");
    const selectionCommentLabel = localizer("discussion.selection.comment");
    const condensedMaxHeight = DISCUSSION_CONDENSED_MAX_HEIGHT_PX;
    const shouldRenderDiscussionContent = !isDeleted;
    const articleRegionId = `discussion-content-article-${useId()}`;

    /**
     * Does collapsing this body actually do anything? (`DISCUSSION-12`)
     *
     * Measured on the injected body element rather than on the `<article>`: the article carries the
     * `max-height` when condensed, so measuring it while collapsed answers "280" whatever the body
     * is. `overflow: hidden` clips the child, it does not shrink it, so the child's own `scrollHeight`
     * is the natural height in both states.
     *
     * Re-measured through a `ResizeObserver` because the answer changes after first paint: a body
     * whose images have not decoded yet is shorter than the body the reader ends up with.
     */
    const [bodyExceedsClamp, setBodyExceedsClamp] = useState(false);
    useEffect(() => {
        const node = bodyRef.current;
        if (!node) {
            setBodyExceedsClamp(false);
            return;
        }

        const measure = () => setBodyExceedsClamp(node.scrollHeight > condensedMaxHeight);
        measure();

        if (typeof ResizeObserver === "undefined") {
            return;
        }

        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [condensedMaxHeight, contentHtml, shouldRenderDiscussionContent]);

    /**
     * The toggle renders only when it would move something.
     *
     * Before 2026-08-17 it was gated on `!isDeleted` alone, so every permalink opened offering to hide
     * the article the reader had just navigated to, and on a body under the clamp clicking it flipped
     * the label and `aria-expanded` while nothing moved — a control that reads as broken because the
     * only recovery is to click it again and watch nothing happen twice.
     *
     * `condensedLocal` is part of the condition so a body that is collapsed can always be re-opened,
     * even in the environments where the measurement never runs (no `ResizeObserver`, a body that has
     * not laid out yet).
     */
    const shouldRenderCollapseToggle = shouldRenderDiscussionContent && (bodyExceedsClamp || condensedLocal);
    const shouldShowDiscussionVisibilityBanner = Boolean(discussionVisibilityBanner) && !isReadOnly;
    const seriesPageHref = useMemo(() => {
        const seriesSlugOrPublicId = seriesReadingContext?.seriesSlug?.trim() || seriesReadingContext?.seriesPublicId?.trim();
        return seriesSlugOrPublicId ? `/series/${encodeURIComponent(seriesSlugOrPublicId)}` : undefined;
    }, [seriesReadingContext?.seriesPublicId, seriesReadingContext?.seriesSlug]);
    const previousDiscussionHref = useMemo(() => buildSeriesDiscussionHref(
        seriesReadingContext?.previousDiscussion?.discussionSlugOrPublicId,
        { contextSeriesPublicId: seriesReadingContext?.seriesPublicId },
    ), [seriesReadingContext?.previousDiscussion?.discussionSlugOrPublicId, seriesReadingContext?.seriesPublicId]);
    const nextDiscussionHref = useMemo(() => buildSeriesDiscussionHref(
        seriesReadingContext?.nextDiscussion?.discussionSlugOrPublicId,
        { contextSeriesPublicId: seriesReadingContext?.seriesPublicId },
    ), [seriesReadingContext?.nextDiscussion?.discussionSlugOrPublicId, seriesReadingContext?.seriesPublicId]);
    const seriesNavigation = useMemo(() => {
        if (!seriesReadingContext?.seriesPublicId?.trim() || !seriesPageHref) {
            return undefined;
        }

        return {
            backToSeriesHref: seriesPageHref,
            backToSeriesTitle: seriesReadingContext?.seriesTitle?.trim() || undefined,
            previous: seriesReadingContext?.previousDiscussion?.discussionSlugOrPublicId?.trim()
                ? {
                    href: previousDiscussionHref,
                    title: seriesReadingContext.previousDiscussion.title?.trim() || undefined,
                }
                : undefined,
            next: seriesReadingContext?.nextDiscussion?.discussionSlugOrPublicId?.trim()
                ? {
                    href: nextDiscussionHref,
                    title: seriesReadingContext.nextDiscussion.title?.trim() || undefined,
                }
                : undefined,
        };
    }, [nextDiscussionHref, previousDiscussionHref, seriesPageHref, seriesReadingContext?.nextDiscussion?.discussionSlugOrPublicId, seriesReadingContext?.nextDiscussion?.title, seriesReadingContext?.previousDiscussion?.discussionSlugOrPublicId, seriesReadingContext?.previousDiscussion?.title, seriesReadingContext?.seriesPublicId, seriesReadingContext?.seriesTitle]);

    const openCommentDialogFromSelection = useCallback((selectedQuote: string) => {
        const selectedQuoteTrimmed = selectedQuote.trim();

        if (!selectedQuoteTrimmed || isReadOnly) {
            return;
        }

        // QA-7: the popover is offered to signed-out readers too, and by the time this runs it has
        // already hidden itself and cleared the selection, so returning here was a tap that did nothing.
        // Ask them to sign in with the reply composer's own copy and continuation builder. The post body
        // has no parent reply, so the builder gives its page-level answer (`null`): back to this post.
        if (!isUserLoggedIn) {
            try {
                openAccountAccessDialog(localizer, resolveContextualAccountAccessDialogOptions(localizer, {
                    ...ACCOUNT_ACCESS_DIALOG_COPY.writeArgument,
                    surface: "discussion_selection_comment",
                    continuation: buildWriteThoughtContinuation({}),
                }));
            } catch (err) {
                logger.error("[DiscussionContentDisplayer] failed to open the sign-in ask for a selection comment", err);
            }
            return;
        }

        let controller = dialogControllerRef.current;

        if (!controller) {
            controller = showThoughtTextEditorDialog({
                localizer,
                autoFocus: true,
                isUserLoggedIn,
                loggedInUsername: MetaHandler.getLoggedInUsername() ?? undefined,
                profileImageUrl: undefined,
                discussionPublicId: props.discussionPublicId,
                agreementAgreementTitle,
                disagreementAgreementTitle,
            });
            dialogControllerRef.current = controller;
        }

        try {
            controller.open();
            (controller as DialogController & { addQuote?: (text?: string) => void }).addQuote?.(selectedQuoteTrimmed);
        } catch (err) {
            logger.error("[DiscussionContentDisplayer] failed to open selection comment dialog", err);
        }
    }, [
        agreementAgreementTitle,
        disagreementAgreementTitle,
        isReadOnly,
        isUserLoggedIn,
        localizer,
        props.discussionPublicId,
    ]);

    useEffect(() => {
        return () => {
            try {
                dialogControllerRef.current?.cleanup();
            } catch {
                // ignore
            }
            dialogControllerRef.current = null;
        };
    }, []);

    /**
     * PASSAGE anchors, the reading path (design §4.2, D4, A5).
     *
     * The body node is the join target, so it is tracked as STATE rather than read off `bodyRef` in an
     * effect: the ref is assigned during commit and an effect keyed on `contentHtml` would run against
     * the previous node on the render that swaps the HTML. `setBodyNode` above already fans the node out
     * to two owners; this is the third, and the same node.
     */
    const [bodyNode, setBodyNode2] = useState<HTMLDivElement | null>(null);
    const [articleNode, setArticleNode] = useState<HTMLElement | null>(null);
    const setBodyNodeWithPassage = useCallback((node: HTMLDivElement | null) => {
        setBodyNode(node);
        setBodyNode2(node);
    }, [setBodyNode]);
    const passageBlockIds = usePassageBlockIds({
        discussionSlugOrPublicId: props.discussionSlug || props.discussionPublicId,
        providedBlockIds: props.passageBlockIds,
        bodyRoot: bodyNode,
        contentHtml,
        enabled: shouldRenderDiscussionContent,
    });
    // The join itself. Refused (null) whenever the counts differ; re-done when the body node or the list
    // changes AND whenever the body's child list changes underneath the same node (React re-applies
    // `dangerouslySetInnerHTML` without replacing the div, and other enhancers touch the body), because a
    // map of detached elements answers every containment question with "no" while the article still
    // reads as eligible. Content-compared before it is stored, so a re-derivation that changed nothing
    // re-renders nothing. Published to the block registry so the tree's chips can jump without a prop path.
    const [passageBlockMap, setPassageBlockMap] = useState<PassageBlockMap | null>(null);
    useLayoutEffect(() => {
        const recompute = () => {
            const next = mapBlocksToDom(passageBlockIds, bodyNode);
            setPassageBlockMap((previous) => (passageBlockMapsEqual(previous, next) ? previous : next));
        };
        recompute();
        if (!bodyNode || typeof MutationObserver === "undefined") {
            return;
        }
        const observer = new MutationObserver(recompute);
        observer.observe(bodyNode, { childList: true });
        return () => observer.disconnect();
    }, [bodyNode, contentHtml, passageBlockIds]);
    useEffect(() => {
        registerPassageBlocks(props.discussionPublicId, passageBlockMap);
        return () => registerPassageBlocks(props.discussionPublicId, null);
    }, [passageBlockMap, props.discussionPublicId]);

    // Anchors published from THIS page instance, so a new passage comment gets its marker at once. The
    // publish response does not carry the anchor (the composer knows what it sent), and the tree's own
    // list catches up on its next fetch; de-duplicated by public id in PassageMarkers.
    const [locallyAnchoredThoughts, setLocallyAnchoredThoughts] = useState<PassageAnchoredThoughtSummary[]>([]);
    const passageAnchoredThoughts = useMemo<PassageAnchoredThoughtSummary[]>(() => {
        const fromTree = props.passageAnchoredThoughts ?? [];
        return locallyAnchoredThoughts.length === 0 ? fromTree : [...fromTree, ...locallyAnchoredThoughts];
    }, [locallyAnchoredThoughts, props.passageAnchoredThoughts]);

    /**
     * The writing path (design §4.1, A1): a composer anchored to one block. A NEW dialog per anchor rather
     * than the cached quote dialog above, because the anchor is a composer prop fixed at creation and the
     * excerpt changes with every selection. Opened for anonymous readers too: the sign-in ask is the
     * composer's own, at Publish, with the draft kept across it.
     */
    const passageDialogControllerRef = useRef<DialogController | null>(null);
    const openPassageComposer = useCallback((blockId: string, excerpt: string) => {
        if (!blockId || !excerpt || isReadOnly || !props.discussionPublicId) {
            return;
        }
        try {
            passageDialogControllerRef.current?.cleanup();
        } catch {
            // ignore
        }
        const discussionPublicId = props.discussionPublicId;
        try {
            const controller = showThoughtTextEditorDialog({
                localizer,
                autoFocus: true,
                isUserLoggedIn,
                loggedInUsername: MetaHandler.getLoggedInUsername() ?? undefined,
                profileImageUrl: undefined,
                discussionPublicId,
                agreementAgreementTitle,
                disagreementAgreementTitle,
                passageAnchor: { blockId, excerpt },
                playwrightIdPrefix: "passage-composer",
                onThoughtPublished: (published) => {
                    const publicId = published?.publicId?.trim();
                    if (!publicId) {
                        return;
                    }
                    setLocallyAnchoredThoughts((previous) => [
                        ...previous,
                        { publicId, blockId, state: "ANCHORED", authorUsername: published?.username ?? undefined },
                    ]);
                },
            });
            passageDialogControllerRef.current = controller;
            controller.open();
        } catch (err) {
            logger.error("[DiscussionContentDisplayer] failed to open passage comment dialog", err);
        }
    }, [
        agreementAgreementTitle,
        disagreementAgreementTitle,
        isReadOnly,
        isUserLoggedIn,
        localizer,
        props.discussionPublicId,
    ]);
    useEffect(() => {
        return () => {
            try {
                passageDialogControllerRef.current?.cleanup();
            } catch {
                // ignore
            }
            passageDialogControllerRef.current = null;
        };
    }, []);

    /**
     * ⚠️ The `dangerouslySetInnerHTML` object is memoized on the STRING, and must stay so. React 19's
     * `setProp` re-applies `innerHTML` whenever the prop OBJECT changes identity, with no comparison of
     * the string inside (React 18 compared `__html`). A fresh `{__html}` literal in the JSX below meant
     * every re-run of the article memo re-set the body: every `<p>` was replaced, every enhancer's work on
     * it (image permalinks, the lightbox hooks, the footnote previews) was discarded, and the passage
     * block map pointed at detached elements. With the child-list observer above it was a 2-cycle (rule
     * 31): map set → memo re-ran → body reset → observer re-mapped → map set. Found on 2026-09-15 by the
     * passage spec's first run; `RENDER_COMMIT_BUDGET_EXCEEDED island=DiscussionPage` was the signal.
     */
    /**
     * SV-140 (A6, rule 32): an on-device translation of the body is a STATE, rendered through this same
     * container, never a mutation of the server's nodes. It is keyed on the HTML it was made from, so a
     * body that changes underneath it (an edit, a soft navigation) drops it without an effect, and a
     * failure never sets it: the stored HTML is what renders unless a translation RESOLVED.
     */
    const [translation, setTranslation] = useState<(TranslationResult & { forHtml: string }) | null>(null);
    const activeTranslation = translation && translation.forHtml === (contentHtml ?? "") ? translation : null;
    const displayedHtml = activeTranslation ? activeTranslation.html : (contentHtml ?? "");
    const displayedLanguageTag = activeTranslation ? activeTranslation.lang : contentLanguageTag;
    const onTranslationDisplayChange = useCallback((next: TranslationResult | null) => {
        setTranslation(next ? { ...next, forHtml: contentHtml ?? "" } : null);
    }, [contentHtml]);
    // Feature detection once per mount: no translator here means no control and no chunk fetch (rule 27).
    const [translatorPresent] = useState(hasTranslatorBackend);
    const offerTranslation = translatorPresent
        && shouldRenderDiscussionContent
        && !isDeleted
        && Boolean(contentLanguageTag)
        && Boolean(contentHtml);

    const bodyInnerHtml = useMemo(() => ({ __html: displayedHtml }), [displayedHtml]);

    const discussionArticle = useMemo(() => (
        <article
            ref={setArticleNode}
            lang={displayedLanguageTag}
            id={articleRegionId}
            className="tiptap relative discussion-content-article"
            data-playwright-id="discussion-content-article"
            data-passage-anchors={passageBlockMap ? "on" : "off"}
            style={{
                maxHeight: condensedLocal ? `${condensedMaxHeight}px` : undefined,
                overflow: condensedLocal ? "hidden" : undefined,
                transition: "max-height 220ms ease",
            }}
        >
            <div ref={setBodyNodeWithPassage} dangerouslySetInnerHTML={bodyInnerHtml}/>

            {/* PASSAGE markers: a sibling of the body, never inside it (rule 32), and null at zero anchors. */}
            <PassageMarkers
                localizer={localizer}
                discussionSlug={props.discussionSlug}
                blockMap={passageBlockMap}
                anchoredThoughts={passageAnchoredThoughts}
                positionRoot={articleNode}
                onCommentOnBlock={isReadOnly ? undefined : openPassageComposer}
            />

            {condensedLocal && (
                <div
                    className="discussion-condensed-fade absolute left-0 right-0 bottom-0 h-20 pointer-events-none"
                    style={{ background: "var(--content-fade-gradient)" }}
                    aria-hidden
                />
            )}
        </article>
    ), [
        articleNode,
        articleRegionId,
        bodyInnerHtml,
        condensedLocal,
        condensedMaxHeight,
        displayedLanguageTag,
        isReadOnly,
        localizer,
        openPassageComposer,
        passageAnchoredThoughts,
        passageBlockMap,
        props.discussionSlug,
        setBodyNodeWithPassage,
    ]);

    // FI-585: the canonical post URL (the same one Share uses) plus a text fragment for the selection.
    const copyHighlightLink = useCallback((selectionText: string) => {
        if (typeof window === "undefined") {
            return;
        }
        const postPath = props.discussionSlug
            ? `/discussion/${props.discussionSlug}`
            : `/discussion/${props.discussionPublicId}`;
        const url = buildTextFragmentUrl(`${window.location.origin}${postPath}`, selectionText);
        const clipboard = typeof navigator !== "undefined" ? navigator.clipboard : undefined;
        if (!clipboard?.writeText) {
            logger.warn("[DiscussionContentDisplayer] no clipboard for Copy link to highlight");
            return;
        }
        void clipboard.writeText(url)
            .then(() => {
                toast.success(localizer("share_url.copied_to_clipboard"));
            })
            .catch((err) => {
                logger.warn("[DiscussionContentDisplayer] Copy link to highlight failed", err);
            });
    }, [localizer, props.discussionPublicId, props.discussionSlug]);

    const renderSelectionPopover = useCallback(() => {
        return (
            <DiscussionSelectionPopover
                selectionCommentLabel={selectionCommentLabel}
                onCommentSelection={openCommentDialogFromSelection}
                passageBlockMap={passageBlockMap}
                onCommentOnPassage={openPassageComposer}
                onCopyHighlightLink={copyHighlightLink}
                localizer={localizer}
            />
        );
    }, [copyHighlightLink, localizer, openCommentDialogFromSelection, openPassageComposer, passageBlockMap, selectionCommentLabel]);

    return (
        <div
            ref={containerRef}
            className={cn(
                // `border-border`, undiluted. This hairline is the ONLY thing separating the article
                // from the page — the fill difference between `--card` and `--background` measures
                // 1.01:1 in light — and at `/60` it composited to 2.02:1, under the 3:1 the 2026-08-17
                // token raise was for (`DISCUSSION-6`). Do not re-dilute it.
                "discussion-content-displayer relative w-full rounded-2xl border border-border bg-card p-4 sm:p-5 lg:mx-auto max-sm:rounded-none max-sm:border-0",
                DISCUSSION_ARTICLE_MEASURE_CLASS,
            )}
            data-discussion-public-id={props.discussionPublicId ?? ""}
        >
            <div className="w-full max-w-full ">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">

                        {discussionGroupsSummary ? (
                            <div>
                                {discussionGroupsSummary}
                            </div>
                        ) : null}
                        {shouldShowDiscussionVisibilityBanner ? (
                            <div className="mt-3">
                                {discussionVisibilityBanner}
                            </div>
                        ) : null}
                        {/* `text-2xl font-semibold` at mobile, NOT `text-xl font-medium`
                            (`DISCUSSION-29`). `css/07-seavien-content-wrapper.css` resets in-body
                            `h1, h2` to 22px below 640px and gives `h2` weight 600, so at 20px/500 the
                            title of the whole piece rendered SMALLER AND LIGHTER than a section
                            heading the author typed inside it — an `<h1>` styled below the `<h2>`s it
                            contains, on a document a reader scrolls for ~24,000px. 24px/600 is the
                            minimal step that clears the 22px/600 content scale. Desktop was already
                            correct and `sm:text-3xl` is unchanged.

                            The content wrapper is deliberately NOT touched: its own comment records
                            that inflating `h2` toward `h1` was rejected because "a weight step moves
                            no line box", and that decision is about in-body headings.

                            `discussion/discussion.jte`'s server-rendered twin carries the identical
                            class set and MOVES WITH THIS ONE — a crawler renders and indexes the SSR
                            copy before `createRoot()` discards it (rule 32). */}
                        <h1 className="mt-0 text-2xl font-semibold sm:text-3xl">
                            <span className="inline-flex flex-wrap items-center gap-2">
                                {/* Round-33 `2b` (r33 D13, A12): a title-less short post's heading holds the
                                    server's page name, visually hidden, the exact span `discussion.jte`
                                    renders in this position, so the heading is named for a screen reader
                                    and nothing changes on hydration (rule 32). Gated on the page's own
                                    `isShortPostPage`, never re-derived from `title`; the `<h1>` and its
                                    DISCUSSION-29 classes stay as they are, so the badges and layout hold. */}
                                {isShortPostPage && pageName ? (
                                    <span className="sr-only">{pageName}</span>
                                ) : (
                                    <span>{displayTitle}</span>
                                )}
                                {sensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                                {/* QUESTION facet: the same two badges `discussion.jte` server-renders in
                                    this exact position (rule 32 — the SSR copy is discarded by
                                    createRoot(), so these are what a crawler actually indexes). They reuse
                                    SensitiveBadge because it produces the identical pill the JTE hand-writes
                                    — the shared appearance is the point, not the sensitivity semantics. */}
                                {isQuestion ? (
                                    <SensitiveBadge className="py-0" dataPlaywrightId="discussion-question-badge">
                                        {localizer("discussion.question.badge")}
                                    </SensitiveBadge>
                                ) : null}
                                {isQuestion ? (
                                    <SensitiveBadge className="py-0" dataPlaywrightId="discussion-question-state-badge">
                                        {localizer(questionStateKey)}
                                    </SensitiveBadge>
                                ) : null}
                                {/* CHANGED MY MIND: the same badge `discussion.jte` server-renders in
                                    this exact position (rule 32 - the SSR copy is discarded by
                                    createRoot(), so this is what a reader sees after hydration and the
                                    JTE twin is what a crawler indexes). Reuses SensitiveBadge for the
                                    identical pill its question siblings use; the shared appearance is
                                    the point, not the sensitivity semantics.

                                    Names the post author, who is the only account that can mark, and
                                    falls back to [Deleted User] rather than disappearing when that
                                    account has been erased - the mark is a record and it outlives the
                                    account that made it.

                                    DISCUSSION-101 (A44): a handle's case is data, so the badge's caps and
                                    letter-spacing stop at the name. localizeWithMemberName wraps the handle in
                                    a translate="no" span (the [Deleted user] fallback is our copy and stays
                                    unwrapped, so it keeps the badge style); the descendant variant resets
                                    exactly that span. The JTE twin renders the same span. */}
                                {mindChanged ? (
                                    <SensitiveBadge className="py-0 [&_[translate=no]]:normal-case [&_[translate=no]]:tracking-normal" dataPlaywrightId="discussion-post-mind-changed-badge">
                                        {localizeWithMemberName(localizer, "discussion.mind_changed.post.badge", displayAuthorUsername)}
                                    </SensitiveBadge>
                                ) : null}
                            </span>
                        </h1>

                        {/* REVIEW facet. Between the title and the byline: the reader has just read
                            what the post is called and needs to know what it is ABOUT before who wrote
                            it. Renders nothing at all unless both halves are present — ReviewHeader
                            self-gates, so a post with a subject but no rating shows no half-drawn
                            strip. */}
                        {reviewSubjectName && reviewRatingHalfStars ? (
                            <ReviewHeader
                                localizer={localizer}
                                subjectName={reviewSubjectName}
                                subjectSlug={reviewSubjectSlug}
                                ratingHalfStars={reviewRatingHalfStars}
                            />
                        ) : null}

                        <AuthorAttribution localizer={localizer}
                            name={displayAuthorUsername}
                            href={authorProfilePath}
                            imageUrl={authorProfileImageUrl}
                            avatarSeed={authorUsername ?? displayAuthorUsername}
                            // Only when there is something to show, so the name keeps no trailing space.
                            nameAdornment={authorFeaturedDistinction || authorVoiceOfMonthGroupName ? (
                                <DistinctionBadge
                                    localizer={localizer}
                                    featuredDistinction={authorFeaturedDistinction}
                                    voiceOfMonthGroupName={authorVoiceOfMonthGroupName}
                                    playwrightId="discussion-author-distinction"
                                />
                            ) : undefined}
                            details={(
                                <>
                                    {/* Reading time sits right after the author (D5 / A4, 2026-09-16):
                                        "author · 3 min read · Started …". Hidden under a minute, and the
                                        separator is gated on the same predicate so nothing dangles. */}
                                    <ReadingTimeLabel localizer={localizer} bodyTextLength={bodyTextLength} />
                                    {startedLabel ? (
                                        <>
                                            {hasReadingTime(bodyTextLength) ? <span aria-hidden={true}>{" · "}</span> : null}
                                            <time className="whitespace-nowrap" dateTime={createdAt} title={createdAtAbsolute}>{startedLabel}</time>
                                        </>
                                    ) : null}
                                    {showUpdatedLabel ? (
                                        <>
                                            {startedLabel || hasReadingTime(bodyTextLength) ? <span aria-hidden={true}>{" · "}</span> : null}
                                            <time className="whitespace-nowrap" dateTime={updatedAt} title={updatedAtAbsolute}>{updatedLabel}</time>
                                        </>
                                    ) : null}
                                    {editedAfterReplyLabel ? (
                                        <>
                                            {startedLabel || showUpdatedLabel || hasReadingTime(bodyTextLength) ? (
                                                <span aria-hidden={true}>{" · "}</span>
                                            ) : null}
                                            <time
                                                dateTime={editedAfterReplyAt}
                                                data-playwright-id="discussion-edited-after-replies"
                                            >
                                                {editedAfterReplyLabel}
                                            </time>
                                        </>
                                    ) : null}
                                </>
                            )}
                            className="mt-2"
                            avatarClassName="size-7"
                            dataPlaywrightId="discussion-author-attribution"
                        />

                        {/*
                          * The AUTHOR's own edit history, directly beneath the edited marker
                          * (operator decision D5 — owner-only). It renders nothing at all for anyone
                          * else and asks the server for nothing until it is opened. `canEdit` gates
                          * only the CONTROL; the guarantee is the endpoint's 403.
                          */}
                        {/* SV-141 D1: not on a short post's page, which drops the long-post chrome. */}
                        {isShortPostPage ? null : (
                            <DiscussionVersionHistory
                                localizer={localizer}
                                discussionPublicId={props.discussionPublicId}
                                canEdit={!!canEdit}
                            />
                        )}

                    </div>

                    {/* Removed the SimpleSelect from the top-right header; it's now in the controls row below */}
                    <div className="ml-4" aria-hidden/>
                </div>

                <div className="mt-4">

                    <div className={cn(
                        "",
                        isDeleted && "bg-[color-mix(in_srgb,var(--destructive)_3%,var(--card))]"
                    )}>
                        {isDeleted ? (() => {
                            const banner = (
                                <div
                                    className={cn(
                                        "rounded-2xl border border-[var(--error-border)] bg-destructive/6 px-4 py-3 text-sm text-foreground",
                                        !viewerIsSelfDeletingAuthor && "mb-4",
                                    )}
                                    data-playwright-id="deleted-discussion-banner"
                                >
                                    <div className="text-muted-foreground">
                                        {resolvedDeletedMessage}
                                        {deletedAt ? (
                                            <>
                                                {" "}
                                                {deletedRelativeLabel}{" "}
                                                <DateFormatterDisplay localizer={localizer} date={deletedAt} relative className="font-medium text-foreground" />
                                                .
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                            );
                            /* `EMPTY-STATES-78` door 3 (D33): the restore control, as the second call
                               site of `DeletedContentRestoreRow` — the dashboard's deleted sub-tab is
                               the first. The row deliberately keeps the affordance out of the card
                               displayers; this is the post's own 410 page, gated on the same predicate
                               the server enforces, so it is the other place the control is correct.
                               `onRestored` reloads: the restored post renders live through the normal
                               server path rather than a hand-built optimistic state. */
                            return viewerIsSelfDeletingAuthor && props.discussionPublicId ? (
                                <div className="mb-4">
                                    <DeletedContentRestoreRow
                                        publicId={props.discussionPublicId}
                                        kind="discussion"
                                        localizer={localizer}
                                        onRestored={() => window.location.reload()}
                                    >
                                        {banner}
                                    </DeletedContentRestoreRow>
                                </div>
                            ) : banner;
                        })() : null}
                        {/* The board, ABOVE the body — the same position `discussion.jte` renders it in.
                            Outside the deleted branch on purpose: a deleted post shows its banner and
                            nothing else. */}
                        {!isDeleted && props.boardSlot && !props.boardAfterBody ? props.boardSlot : null}
                        {!isDeleted && props.pollSlot ? props.pollSlot : null}
                        {shouldRenderDiscussionContent ? (
                            isReadOnly ? discussionArticle : (
                                <HighlightPopover
                                    alignment="center"
                                    minSelectionLength={1}
                                    zIndex={Z_INDEX_LAYER_STICKY}
                                    renderPopover={renderSelectionPopover}
                                >
                                    {discussionArticle}
                                </HighlightPopover>
                            )
                        ) : null}
                        {/* SV-140: purely additive, below the body; renders nothing unless the device can
                            translate this post into another language (A1). */}
                        {offerTranslation ? (
                            <TranslateControlLazy
                                html={contentHtml ?? ""}
                                sourceLang={contentLanguageTag}
                                localizer={localizer}
                                onDisplayChange={onTranslationDisplayChange}
                                entity={props.discussionPublicId ? { type: "DISCUSSION", publicId: props.discussionPublicId } : null}
                            />
                        ) : null}
                        {/* BOARD-21 (D17): a complete board's grid follows the body, where `discussion.jte`
                            renders it for the same board. */}
                        {!isDeleted && props.boardSlot && props.boardAfterBody ? props.boardSlot : null}
                    </div>
                </div>

                <DiscussionFootnotePreviews contentRootRef={contentRootRef} localizer={localizer} />

                {mediaLightbox}

                {shouldRenderCollapseToggle ? (
                    <div className="mt-2 flex flex-row justify-end">
                        <CtaButton
                            icon={condensedLocal ? ChevronsUpDown : ChevronsDownUp}
                            text={condensedLocal ? readMore : readLess}
                            extraClass="toggle-discussion-content-btn"
                            variant="ghost"
                            onClick={() => setCondensedLocal(prev => !prev)}
                            aria-expanded={!condensedLocal}
                            aria-controls={articleRegionId}
                            aria-label={condensedLocal ? readMore : readLess}
                            size="sm"
                        />
                    </div>
                ) : null}

                {props.tagNames && props.tagNames.length > 0 ? (
                    /*
                     * No heading, deliberately — which is also why this costs ZERO new catalog keys.
                     * The JTE block carries an English "Topics" heading for the crawl pass; a member
                     * reading a post does not need a row of hashtags labelled.
                     *
                     * Classes mirror the JTE anchor exactly, so the rendered result does not change
                     * when the island mounts over the server's markup. That mirroring is a COPIED
                     * STRING with no compile-time link, and on 2026-09-17 it broke in the one
                     * direction nobody looks at: `[@media(pointer:coarse)]:min-h-11` was added to
                     * `MainService/src/main/jte/discussion/discussion.jte` and not here, so the
                     * hardened chip lived only until `createRoot()` deleted it (FI-320 / FS-8).
                     * Both halves are now in `RATCHETED_FILES` in
                     * `src/components/ui/tapTargets.contract.test.tsx`, which fails if either one
                     * drops a control under the 44px floor. Change this string and that one together.
                     */
                    <div className="mt-4 flex flex-wrap gap-2" data-playwright-id="discussion-topic-chips">
                        {props.tagNames.map((tagName) => (
                            <a
                                key={tagName}
                                className="standard-link inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs [@media(pointer:coarse)]:min-h-11"
                                href={`/tag/${encodeURIComponent(tagName)}`}
                            >
                                #{tagName}
                            </a>
                        ))}
                    </div>
                ) : null}

                <div className="mt-3 flex w-full flex-row flex-wrap items-center justify-end gap-3 border-t border-border pt-3">
                    <div className="ml-auto flex max-w-full flex-wrap justify-end">
                        <DiscussionButtons
                            discussionPublicId={props.discussionPublicId ?? ""}
                            discussionSlug={props.discussionSlug ?? undefined}
                            initialAgreementsCount={agreementsCount ?? 0}
                            initialDisagreementsCount={disagreementsCount ?? 0}
                            initialArgumentsCount={argumentsCount ?? 0}
                            initialAddedToSeriesCount={addedToSeriesCount ?? 0}
                            agreementAgreementTitle={agreementAgreementTitle}
                            disagreementAgreementTitle={disagreementAgreementTitle}
                            discussionTitle={shareTitle}
                            authorDisplayName={authorUsername}
                            authorUsername={authorUsername}
                            isPrivateDiscussion={Boolean(privateAccess)}
                            initiallyAgreeing={!!initiallyAgreeing}
                            initiallyDisagreeing={!!initiallyDisagreeing}
                            initiallyAddedToAnySeries={!!initiallyAddedToAnySeries}
                            initiallySavedToBookmarks={!!initiallySavedToBookmarks}
                            isArchived={isArchived}
                            isDeleted={isDeleted}
                            localizer={localizer}
                            canEdit={!!canEdit}
                            showManageActions={true}
                            seriesNavigation={seriesNavigation}
                            showFloatingMobileBar={true}
                            mobileSectionNavigation={mobileSectionNavigation}
                            isQuestion={isQuestion}
                            questionState={questionState}
                            onQuestionStateChanged={onQuestionStateChanged}
                            askToAnswerAvailable={askToAnswerAvailable}
                            reviewSubjectPublicId={reviewSubjectPublicId}
                            reviewSubjectName={reviewSubjectName}
                            reviewRatingHalfStars={reviewRatingHalfStars}
                            onReviewFacetChanged={onReviewFacetChanged}
                            endOfPostAnchors={endOfPostAnchors}
                            isShortPost={isShortPostPage}
                        />
                    </div>
                </div>
                {/* End-of-post reply row anchor (row 2), between the post and the thoughts. */}
                <div ref={setEndOfPostReplyRowAnchor} className="mt-3 empty:hidden"/>
            </div>
        </div>
    );
}

export default DiscussionContentDisplayer;
